import React, {
    useMemo,
    useRef,
    useState,
    useEffect,
    useImperativeHandle,
    forwardRef,
} from 'react';
import { Runner } from './core/runner';
import type { EngineBridge, LanguageAdapter, LanguageId } from './core/types';
import { createWindowBridge, makeNoopBridge } from './core/bridge';
import { JavascriptAdapter } from './adapters/javascript/adapter';
import { PythonPyodideAdapter } from './adapters/python-pyodide/adapter';

import CodeMirror from '@uiw/react-codemirror';
import { Button, Tooltip, Drawer } from 'antd';
import {
    CaretRightOutlined,
    QuestionCircleOutlined,
    RollbackOutlined,
    StopOutlined,
    SettingOutlined,
} from '@ant-design/icons';
import HelpConfiguratorDrawer from './helpCode/helpDrawer';
import CodeHelp from './helpCode/codeHelp';
import HelpPanel from './helpObject/HelpPanel';
import { TObstacle, TScene } from '../../../models/objects/objects';
import { TSprite } from '../../../models/objects/sprite';
import { TSound } from '../../../models/objects/media';

import {
    EditorView,
    highlightActiveLine,
    highlightActiveLineGutter,
} from '@codemirror/view';
import { vscodeDark } from '@uiw/codemirror-theme-vscode';
import { keymap } from '@codemirror/view';
import { defaultKeymap, history, historyKeymap } from '@codemirror/commands';
import { customScrollbar } from './editor/themeScrollbar';

// tweak mobile léger (line numbers)
const mobileTweak = EditorView.theme({
    '.cm-gutters': { width: '28px' },
    '.cm-lineNumbers': { minWidth: '20px', fontSize: '11px' },
});

declare global {
    interface Window {
        __TILMI_EVENTS__?: {
            on: (name: string, handler: (...args: any[]) => void) => () => void;
            once: (name: string, handler: (...args: any[]) => void) => () => void;
            off: (offFn?: () => void) => void;
        };
    }
}

// ---------- Public API (ref) ----------
export interface UnifiedIDERef {
    getCode: () => string;
    getConfig: () => {
        initialCode: string;
        helpConfig: Record<string, any>; // par langage
        helpGrouped?: boolean;
    };
}

// ---------- Props ----------
export interface UnifiedIDEProps {
    language: LanguageId;
    initialCode: string;

    projectMode?: boolean;
    consoleMode?: boolean;

    scenesConfig: TScene[];
    sprites: TSprite[];
    obstacles: TObstacle[];
    sounds: TSound[];

    adminMode?: boolean;
    enable?: boolean;

    bridge?: EngineBridge;

    helpProps?: any;

    helpConfig?: any;
    helpGrouped?: boolean;
    helpStorageKey?: string | null;

    onCodeChange?: (code: string) => void;
    onRestoreInitial?: () => void;
    onLog?: (msg: any) => void;
    onStop?: () => void;
    onPersistHelpConfig?: (
        lang: LanguageId,
        cfg: any,
        grouped: boolean
    ) => void;

    windowBridgeKey?: string; // default "__IDE_BRIDGE__"
}

// ---------- Adapters ----------
function pickAdapter(lang: LanguageId): LanguageAdapter {
    if (lang === 'python') return PythonPyodideAdapter;
    return JavascriptAdapter;
}

// ---------- LocalStorage helpers ----------
const HELP_STORAGE_KEY = (lang: string, scope?: string) =>
    `UnifiedIDE_help_config_${lang}${scope ? `_${scope}` : ''}`;
const GROUPED_STORAGE_KEY = (lang: string, scope?: string) =>
    `UnifiedIDE_help_grouped_${lang}${scope ? `_${scope}` : ''}`;

const EMPTY_HELP_CONFIG = {
    activeCategories: [],
    activeSnippets: {},
};

export const UnifiedIDE = forwardRef<UnifiedIDERef, UnifiedIDEProps>(
    (
        {
            language,
            initialCode,
            projectMode = false,
            consoleMode = false,
            adminMode = false,
            scenesConfig,
            sprites,
            obstacles,
            sounds,

            enable = true,
            bridge,
            helpProps,
            helpConfig,
            helpGrouped,
            helpStorageKey,
            onCodeChange,
            onRestoreInitial,
            onLog,
            onStop,
            onPersistHelpConfig,
            windowBridgeKey = '__IDE_BRIDGE__',
        },
        ref
    ) => {
        const adapter = useMemo(() => pickAdapter(language), [language]);
        const isMobile = window.innerWidth <= 768;

        // ---------- Code state ----------
        const [code, setCode] = useState<string>(initialCode);
        const [status, setStatus] = useState<string>('idle');
        const [isRunning, setIsRunning] = useState(false);
        const [isFormatting, setIsFormatting] = useState(false);
        const [errors] = useState<{ line: number; message: string }[]>([]);

        // ---------- Help drawers ----------
        const [helpVisible, setHelpVisible] = useState(false);
        const [adminHelpVisible, setAdminHelpVisible] = useState(false);

        // ---------- Help config state (multi-langue) ----------
        const [helpConfigByLang, setHelpConfigByLang] = useState<Record<string, any>>({});
        const [helpGroupedState, setHelpGroupedState] = useState<boolean>(!!helpGrouped);

        const helpStorageScope = helpStorageKey === null ? null : helpStorageKey;
        const helpConfigStorageKey = useMemo(() => {
            if (helpStorageScope === null) return null;
            return HELP_STORAGE_KEY(language, helpStorageScope);
        }, [language, helpStorageScope]);
        const helpGroupedStorageKey = useMemo(() => {
            if (helpStorageScope === null) return null;
            return GROUPED_STORAGE_KEY(language, helpStorageScope);
        }, [language, helpStorageScope]);
        
        // Bridge final : props > window > noop
        // const runtimeBridge: EngineBridge = useMemo(
        //     () =>
        //         bridge ??
        //         createWindowBridge(windowBridgeKey, (m) => onLog?.(m)) ??
        //         makeNoopBridge((m) => onLog?.(m)),
        //     [bridge, onLog, windowBridgeKey]
        // );

        const resolveBridge = () =>
            (bridge ??
                createWindowBridge(windowBridgeKey, (m) => onLog?.(m)) ??
                makeNoopBridge((m) => onLog?.(m))) as EngineBridge;

        // Runner
        const runnerRef = useRef<Runner | null>(null);
        if (!runnerRef.current) {
            runnerRef.current = new Runner(adapter);
            (runnerRef.current as any).__lang = adapter.id;
        } else if ((runnerRef.current as any).__lang !== adapter.id) {
            runnerRef.current.setAdapter(adapter);
            (runnerRef.current as any).__lang = adapter.id;
        }
        const runner = runnerRef.current;

        // ---------- Sync initialCode -> code ----------
        useEffect(() => {
            setCode(initialCode);
        }, [initialCode]);

        // ---------- Breakpoints from gutter ----------
        useEffect(() => {
            const handler = (e: Event) => {
                const detail = (e as CustomEvent).detail as { line1: number };
                runner.breakpoints().toggle(detail.line1);
                setStatus((s) => s); // re-render
            };
            document.addEventListener('cm-toggle-breakpoint', handler as any);
            return () => {
                document.removeEventListener('cm-toggle-breakpoint', handler as any);
            };
        }, [runner]);

        // ---------- onStop hook ----------
        useEffect(() => {
            const unsub = runner.onStop(() => {
                setIsRunning(false);
                setStatus('stopped');
                onStop?.();
            });
            return () => {
                unsub();
            };
        }, [runner, onStop]);

        // ---------- Charger la config d'aide/grouped ----------
        useEffect(() => {
            // help config
            if (helpConfigStorageKey) {
                const raw = localStorage.getItem(helpConfigStorageKey);
                if (raw) {
                    try {
                        const parsed = JSON.parse(raw);
                        setHelpConfigByLang((prev) => ({
                            ...prev,
                            [language]: parsed,
                        }));
                    } catch {
                        if (helpConfig !== undefined) {
                            setHelpConfigByLang((prev) => ({
                                ...prev,
                                [language]: helpConfig,
                            }));
                        } else {
                            setHelpConfigByLang((prev) => ({
                                ...prev,
                                [language]: EMPTY_HELP_CONFIG,
                            }));
                        }
                    }
                } else if (helpConfig !== undefined) {
                    setHelpConfigByLang((prev) => ({
                        ...prev,
                        [language]: helpConfig,
                    }));
                } else {
                    setHelpConfigByLang((prev) => ({
                        ...prev,
                        [language]: EMPTY_HELP_CONFIG,
                    }));
                }
            } else if (helpConfig !== undefined) {
                setHelpConfigByLang((prev) => ({
                    ...prev,
                    [language]: helpConfig,
                }));
            } else {
                setHelpConfigByLang((prev) => ({
                    ...prev,
                    [language]: EMPTY_HELP_CONFIG,
                }));
            }

            // grouped
            if (helpGroupedStorageKey) {
                const g = localStorage.getItem(helpGroupedStorageKey);
                if (g !== null) {
                    try {
                        setHelpGroupedState(JSON.parse(g));
                    } catch {
                        setHelpGroupedState(!!helpGrouped);
                    }
                } else if (helpGrouped !== undefined) {
                    setHelpGroupedState(!!helpGrouped);
                } else {
                    setHelpGroupedState(false);
                }
            } else if (helpGrouped !== undefined) {
                setHelpGroupedState(!!helpGrouped);
            } else {
                setHelpGroupedState(false);
            }
        }, [language, helpConfig, helpGrouped, helpConfigStorageKey, helpGroupedStorageKey]);


        // ---------- Raccourcis clavier: Run / Stop ----------
        useEffect(() => {
            const onKey = (e: KeyboardEvent) => {
                if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
                    e.preventDefault();
                    if (!isRunning) runAll();
                } else if (e.key === 'Escape') {
                    e.preventDefault();
                    if (isRunning) stop();
                }
            };
            window.addEventListener('keydown', onKey);
            return () => window.removeEventListener('keydown', onKey);
        }, [isRunning]);

        useEffect(() => {
            // table de correspondance DSL -> événements DOM globaux
            const EVT_MAP: Record<string, string> = {
                collision: 'tilmi:collisionEvent',
                'collision:char-char': 'tilmi:collision:char-char',
                'collision:char-immovable': 'tilmi:collision:char-immovable',
                'collision:char-push': 'tilmi:collision:char-push',
                'collision:obj-obj': 'tilmi:collision:obj-obj',
                pickup: 'tilmi:pickupEvent',
                'object:picked': 'tilmi:object:picked',
                key: 'tilmi:keyPressEvent',
                textChange: 'tilmi:textChangeEvent',
                reach: 'tilmi:reachPositionEvent',
            };

            const toDomEvt = (name: string) => EVT_MAP[name] ?? `tilmi:${name}`;

            const makeWrapper = (handler: (...args: any[]) => void) => (e: Event) => {
                const detail = (e as CustomEvent).detail;
                if (Array.isArray(detail)) handler(...detail);
                else handler(detail);
            };

            const api = {
                on(name: string, handler: (...args: any[]) => void) {
                    const domEvt = toDomEvt(name);
                    const wrapped = makeWrapper(handler);
                    window.addEventListener(domEvt, wrapped as EventListener);
                    return () => window.removeEventListener(domEvt, wrapped as EventListener);
                },
                once(name: string, handler: (...args: any[]) => void) {
                    const domEvt = toDomEvt(name);
                    const wrapped = makeWrapper((...args) => {
                        off();
                        handler(...args);
                    });
                    const off = () => window.removeEventListener(domEvt, wrapped as EventListener);
                    window.addEventListener(domEvt, wrapped as EventListener, { once: true });
                    return off;
                },
                off(offFn?: () => void) {
                    offFn?.();
                },
            } as const;

            if (!window.__TILMI_EVENTS__) window.__TILMI_EVENTS__ = api;
        }, []);

        function snapshotBridge(b: any) {
            const g = window as any;
            const gl = g.__IDE_GLOBALS__;
            const br = b || {};
            const api = (br as any).api;

            const info = {
                // Globals exposés par Phaser (via functions.exposeIDEGlobals)
                hasGlobals: !!gl,
                globalsCount: gl ? Object.keys(gl).length : 0,
                globalsSample: gl ? Object.keys(gl).slice(0, 12) : [],

                // Bridge trouvé (window.__IDE_BRIDGE__ ou props.bridge)
                hasBridge: !!br,
                hasBridgeEvents: !!(br as any)?.events?.on && !!(br as any)?.events?.off && !!(br as any)?.events?.emit,

                // API portée par le bridge (facultatif selon ton design)
                hasBridgeApi: !!api && typeof api === 'object',
                bridgeApiCount: api ? Object.keys(api).length : 0,
                bridgeApiSample: api ? Object.keys(api).slice(0, 12) : [],
            };

            // aide visuelle
            console.groupCollapsed('[IDE diag] Bridge snapshot');
            console.table(info);
            console.groupEnd();

            return info;
        }

        // ---------- Exécution ----------
        const runAll = async () => {
            try {

                runner.setIgnoreBreakpoints(false);
                const liveBridge = resolveBridge();
                // const snap = snapshotBridge(liveBridge);
                await runner.compile(code, liveBridge);
                setIsRunning(true);
                setStatus('running');
                void runner.runAll();
            } catch (e) {
                console.error('UnifiedIDE: Execution error:', e);
                setStatus('error');
                onLog?.(e);
                setIsRunning(false);
            }
        };

        const stop = () => {
            runner.stop(); // onStop effect mettra isRunning=false + reset status
        };

        // ---------- Insert snippet helper ----------
        const handleInsertSnippet = (snippet: string) => {
            setCode((prev) => (prev.endsWith('\n') ? prev + snippet : prev + '\n' + snippet));
            onCodeChange?.(code);
        };

        // ---------- Imperative Handle ----------
        useImperativeHandle(
            ref,
            () => ({
                getCode: () => code,
                getConfig: () => ({
                    initialCode: code,
                    helpConfig: helpConfigByLang,
                    helpGrouped: helpGroupedState,
                }),
            }),
            [code, helpConfigByLang, helpGroupedState]
        );

        const statusColor =
            status === 'running'
                ? '#22c55e'
                : status === 'error'
                    ? '#ef4444'
                    : status === 'stopped'
                        ? '#eab308'
                        : '#94a3b8';

        return (
            <div
                style={{
                    display: 'flex',
                    flexDirection: 'column',
                    height: '100%',
                    width: '100%',
                    backgroundColor: '#0b0d10',
                    border: '1px solid #1f2937',
                    borderRadius: 10,
                    overflow: 'hidden',
                    boxShadow: '0 10px 30px rgba(0,0,0,0.35)',
                }}
            >
                {/* Toolbar (sticky) - Hidden in console mode */}
                {!consoleMode && (
                    <div
                        role="toolbar"
                        style={{
                            position: 'sticky',
                            top: 0,
                            zIndex: 30,
                            padding: '8px 10px',
                            background:
                                'linear-gradient(180deg, rgba(31,41,55,0.85) 0%, rgba(17,24,39,0.85) 100%)',
                            borderBottom: '1px solid #1f2937',
                            backdropFilter: 'blur(6px)',
                            WebkitBackdropFilter: 'blur(6px)',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 8,
                        }}
                    >
                        {/* Ligne outils */}
                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 8,
                                width: '100%',
                                flexWrap: 'wrap',
                            }}
                        >
                            {!projectMode && (
                                <Tooltip title="Rétablir le code initial">
                                    <Button
                                        icon={<RollbackOutlined />}
                                        onClick={() => {
                                            setCode(initialCode);
                                            onRestoreInitial?.();
                                        }}
                                        ghost
                                    >
                                        {!isMobile && 'Rétablir'}
                                    </Button>
                                </Tooltip>
                            )}

                            {/* Groupe actions à droite */}
                            <div
                                style={{
                                    marginLeft: 'auto',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 8,
                                    flexWrap: 'wrap',
                                    flexShrink: 0,
                                }}
                            >
                                {/* Bouton Format */}
                                <Tooltip title="Formater">
                                    <Button
                                        icon={<SettingOutlined />}
                                        onClick={async () => {
                                            const maybeFormat = (adapter as any).format as
                                                | undefined
                                                | ((s: string) => Promise<string>);
                                            if (!maybeFormat) return;
                                            try {
                                                setIsFormatting(true);
                                                const formatted = await maybeFormat(code);
                                                if (formatted && formatted !== code) {
                                                    setCode(formatted);
                                                    onCodeChange?.(formatted);
                                                }
                                            } finally {
                                                setIsFormatting(false);
                                            }
                                        }}
                                        disabled={isRunning || isFormatting || !(adapter as any).format}
                                        ghost
                                    >
                                        {!isMobile && 'Format'}
                                    </Button>
                                </Tooltip>

                                {/* Run / Stop */}
                                <Tooltip title={isRunning ? 'Arrêter (Esc)' : 'Exécuter (Ctrl+Entrée)'}>
                                    <Button
                                        type={isRunning ? 'default' : 'primary'}
                                        danger={isRunning}
                                        icon={isRunning ? <StopOutlined /> : <CaretRightOutlined />}
                                        onClick={() => {
                                            if (!isRunning) runAll();
                                            else stop();
                                        }}
                                        disabled={errors.length > 0 || isFormatting}
                                        style={!isMobile ? { minWidth: 96 } : {}}
                                    >
                                        {!isMobile && (isRunning ? 'Stop' : 'Run')}
                                    </Button>
                                </Tooltip>

                                {/* Aide */}
                                <Tooltip title="Aide des snippets (Ctrl+/)">
                                    <Button icon={<QuestionCircleOutlined />} onClick={() => setHelpVisible(true)} ghost>
                                        {!isMobile && 'Help'}
                                    </Button>
                                </Tooltip>

                                {/* Admin */}
                                {adminMode && (
                                    <Tooltip title="Configurer l’aide (catégories & snippets)">
                                        <Button icon={<SettingOutlined />} onClick={() => setAdminHelpVisible(true)} ghost>
                                            Configurer l’aide
                                        </Button>
                                    </Tooltip>
                                )}
                            </div>
                        </div>

                        {/* HelpPanel optionnel */}
                        {projectMode && !consoleMode && (
                            <div
                                style={{
                                    marginTop: 4,
                                    paddingTop: 8,
                                    borderTop: '1px solid #444',
                                    width: '100%',
                                }}
                            >
                                <HelpPanel
                                    language={language}
                                    sprites={sprites}
                                    scenesConfig={scenesConfig}
                                    obstacles={obstacles}
                                    sounds={sounds}
                                />
                            </div>
                        )}
                    </div>
                )}

                {/* Console Mode Toolbar */}
                {consoleMode && (
                    <div
                        role="toolbar"
                        style={{
                            position: 'sticky',
                            top: 0,
                            zIndex: 30,
                            padding: '8px 10px',
                            background:
                                'linear-gradient(180deg, rgba(31,41,55,0.85) 0%, rgba(17,24,39,0.85) 100%)',
                            borderBottom: '1px solid #1f2937',
                            backdropFilter: 'blur(6px)',
                            WebkitBackdropFilter: 'blur(6px)',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 8,
                        }}
                    >
                        <Tooltip title={isRunning ? 'Arrêter (Esc)' : 'Exécuter (Ctrl+Entrée)'}>
                            <Button
                                type={isRunning ? 'default' : 'primary'}
                                danger={isRunning}
                                icon={isRunning ? <StopOutlined /> : <CaretRightOutlined />}
                                onClick={() => {
                                    if (!isRunning) runAll();
                                    else stop();
                                }}
                                disabled={errors.length > 0 || isFormatting}
                                style={{ minWidth: 96 }}
                            >
                                {isRunning ? 'Stop' : 'Run'}
                            </Button>
                        </Tooltip>

                        <Tooltip title="Rétablir le code initial">
                            <Button
                                icon={<RollbackOutlined />}
                                onClick={() => {
                                    setCode(initialCode);
                                    onRestoreInitial?.();
                                }}
                                ghost
                            >
                                Reset
                            </Button>
                        </Tooltip>
                    </div>
                )}

                {/* Editor */}
                <div
                    dir="ltr" // <<< neutralise tout héritage RTL
                    style={{
                        flex: 1,
                        minHeight: 0,
                        minWidth: 0,
                        display: 'flex',
                        flexDirection: 'column',
                    }}
                >
                    <CodeMirror
                        value={code}
                        theme={vscodeDark}
                        height={isMobile ? '150px' : '100%'}
                        style={{ flex: 1, minHeight: 0 }} 
                        editable={enable && !isRunning && !isFormatting}
                        basicSetup={false}
                        extensions={[
                            history(),                                      // undo/redo
                            keymap.of([...defaultKeymap, ...historyKeymap]),// Enter, Tab, undo/redo, etc.
                            EditorView.lineWrapping,                        // retour à la ligne visuel 
                            adapter.editorExtensions(),                     // tes extensions langage
                            highlightActiveLine(),
                            highlightActiveLineGutter(),
                            customScrollbar,                // ⬅ applique le scroll interne
                            ...(isMobile ? [mobileTweak] : []),
                        ]}
                        onChange={(v) => { setCode(v); onCodeChange?.(v); }}
                    />
                </div>

                {/* Status bar */}
                <div
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 12,
                        padding: '6px 10px',
                        borderTop: '1px solid #1f2937',
                        backgroundColor: '#0f172a',
                        fontSize: 12,
                        color: '#a3b0c2',
                    }}
                >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span
                            style={{
                                display: 'inline-block',
                                width: 8,
                                height: 8,
                                borderRadius: '999px',
                                background: statusColor,
                                boxShadow: `0 0 0 2px rgba(255,255,255,0.05)`,
                            }}
                        />
                        <span>
                            Status:{' '}
                            <b style={{ color: '#e5e7eb' }}>
                                {status}
                                {isFormatting ? ' (formatting...)' : ''}
                            </b>
                        </span>
                    </div>
                    <div>
                        Adapter: <b style={{ color: '#e5e7eb' }}>{adapter.id}</b>
                    </div>
                </div>

                {/* Drawer Aide (utilisateur) */}
                <Drawer
                    title="Aide"
                    placement="right"
                    width={800}
                    onClose={() => setHelpVisible(false)}
                    open={helpVisible}
                >
                    <CodeHelp
                        language={language}
                        config={helpConfigByLang[language]}
                        helpGrouped={helpGroupedState}
                        projectMode={projectMode}
                        onSelectSnippet={handleInsertSnippet}
                        {...(helpProps || {})}
                    />
                </Drawer>

                {/* Drawer Admin (configuration de l’aide) */}
                {adminMode && (
                    <HelpConfiguratorDrawer
                        language={language}
                        visible={adminHelpVisible}
                        onClose={() => setAdminHelpVisible(false)}
                        initialConfig={helpConfigByLang[language]}
                        initialHelpGrouped={helpGroupedState}
                        onToggleHelpGrouped={(val: boolean) => {
                            setHelpGroupedState(val);
                            if (helpGroupedStorageKey) {
                                try {
                                    localStorage.setItem(
                                        helpGroupedStorageKey,
                                        JSON.stringify(val)
                                    );
                                } catch { }
                            }
                        }}
                        onSave={(newCfg: any, newGrouped: boolean) => {
                            const next = { ...helpConfigByLang, [language]: newCfg };
                            setHelpConfigByLang(next);
                            setHelpGroupedState(!!newGrouped);
                            if (helpConfigStorageKey) {
                                try {
                                    localStorage.setItem(helpConfigStorageKey, JSON.stringify(newCfg));
                                } catch { }
                            }
                            if (helpGroupedStorageKey) {
                                try {
                                    localStorage.setItem(
                                        helpGroupedStorageKey,
                                        JSON.stringify(!!newGrouped)
                                    );
                                } catch { }
                            }
                            onPersistHelpConfig?.(language, newCfg, !!newGrouped);
                            setAdminHelpVisible(false);
                        }}
                    />
                )}
            </div>
        );
    }
);
